import { useState } from 'react'
import { Calculator, Database, Upload } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

function WorkflowCard({ icon: Icon, title, description, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full flex-col items-center gap-3 rounded-xl border border-border bg-card/80 p-6 text-center transition-[transform,border-color,background-color] hover:-translate-y-0.5 hover:border-primary/50 hover:bg-accent/50 motion-reduce:transform-none motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary" aria-hidden="true">
        <Icon className="h-5 w-5" />
      </span>
      <span className="space-y-1.5">
        <span className="block text-base font-semibold text-foreground">{title}</span>
        <span className="block text-sm leading-5 text-muted-foreground">{description}</span>
      </span>
    </button>
  )
}

export default function OnboardingStepPeople({
  onStartEstimate,
  onUseExample,
  onStartEmpty,
  onUseExistingClient,
  hasSavedCensus = false,
}) {
  const [pendingReplacement, setPendingReplacement] = useState(null)

  const runReplacement = (replacement) => {
    if (replacement === 'demo') onUseExample()
    if (replacement === 'empty') onStartEmpty()
  }

  const requestReplacement = (replacement) => {
    if (hasSavedCensus) {
      setPendingReplacement(replacement)
      return
    }
    runReplacement(replacement)
  }

  const confirmingDemo = pendingReplacement === 'demo'

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-balance text-2xl font-semibold text-foreground">How Would You Like to Start?</h1>
        <p className="text-sm text-muted-foreground">Choose the option that fits your work today.</p>
      </div>

      <section className="grid gap-3 md:grid-cols-3" aria-label="Choose how to start">
        <WorkflowCard
          icon={Database}
          title="Demo"
          description="Try the calculator with sample employees."
          onClick={() => requestReplacement('demo')}
        />
        <WorkflowCard
          icon={Calculator}
          title="New Prospect"
          description="Create a quick estimate from a few company assumptions."
          onClick={onStartEstimate}
        />
        <WorkflowCard
          icon={Upload}
          title="Existing Client"
          description="Upload an employee file and review the client&rsquo;s plan."
          onClick={onUseExistingClient}
        />
      </section>

      <div className="text-center">
        <button
          type="button"
          onClick={() => requestReplacement('empty')}
          className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Start Without Employees
        </button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Your employee data never leaves this browser.
      </p>

      <AlertDialog open={pendingReplacement !== null} onOpenChange={(open) => {
        if (!open) setPendingReplacement(null)
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmingDemo ? 'Replace the Employee List?' : 'Clear the Employee List?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmingDemo
                ? 'Demo replaces the employee list with sample data. Plan settings are unchanged.'
                : 'This removes all employees. Plan settings are unchanged.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Employee List</AlertDialogCancel>
            <AlertDialogAction
              variant={confirmingDemo ? 'default' : 'destructive'}
              onClick={() => {
                const replacement = pendingReplacement
                setPendingReplacement(null)
                runReplacement(replacement)
              }}
            >
              {confirmingDemo ? 'Use Demo Data' : 'Clear Employee List'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
